import { ReactNode, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import styled from "styled-components";
import { Icon } from "../style/common_style";
import api from '../axios_Interceptor/api';
import url from '../server';
import { subscribeSupportActivity } from "../Support/supportRealtime";
import { useToken } from "../Context/useToken";
import { SellerAdminPermissionKey } from "../adminPermissions";
import { useTheme } from "../../styles/useTheme";

const MenuBox = styled.div`
  background-color: var(--admin-sidebar);
  color: var(--color-sidebar-text-active);
  width: var(--sidebar-width);
  box-sizing: border-box;
  border-right: 1px solid var(--color-sidebar-border);
  flex-shrink: 0;
  height: 100%;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  [data-sidebar-footer] .theme-label > span { display: none; }
  @media (max-height: 760px) {
    [data-sidebar-logo] { padding-block: var(--space-2); }
    [data-sidebar-coin] { display: flex; align-items: center; justify-content: space-between; gap: var(--space-2); }
    [data-sidebar-footer] { padding-block: var(--space-1); }
  }
  @media (max-height: 700px) {
    [data-sidebar-logo] { padding-block: 0; min-height: var(--touch-target); }
    [data-sidebar-footer] .theme-label { display: none; }
    &[data-collapsed="true"] [data-sidebar-footer] { padding: var(--space-1) var(--space-2); }
  }
  div.Logo {
    padding: 2rem 1.5rem;
    cursor: pointer;
  }

  @media (max-width: 900px) { width: 4.75rem; }

  .warehouse-menu-icon { margin-right: var(--space-2); flex-shrink: 0; }
  @media (max-width: 900px) { .warehouse-menu-icon { margin-right: 0; } }
  &[data-collapsed="true"] {
    .warehouse-menu-icon { margin-right: 0; }
    width: 4.75rem;
    [data-sidebar-logo] { padding: var(--space-3); overflow: hidden; }
    nav button { justify-content: center; padding-inline: var(--space-3); position: relative; }
    nav button img { margin-right: 0; }
    .menu-label { display: none; }
    [data-sidebar-footer] { padding: var(--space-3); }
    [data-sidebar-footer] .theme-label > span { display: none; }
    [data-sidebar-footer] button[role="switch"] { flex-direction: column; justify-content: center; min-height: var(--touch-target); }
    [data-sidebar-footer] { gap: 0; }
    [data-sidebar-footer] > button[role="switch"], [data-sidebar-footer] > button:last-child { flex-basis: 100%; width: 100%; }
    [data-sidebar-footer] .theme-label { display: none; }
    @media (max-height: 700px) {
      [data-sidebar-logo] { padding-block: 0; }
      [data-sidebar-footer] { padding: var(--space-1) var(--space-2); }
    }
  }
`;
const LogoButton = styled.button`
  flex-shrink: 0;
  width: 100%;
  padding: var(--space-4) var(--space-5);
  min-height: var(--control-xl);
  display: flex; align-items: center; justify-content: flex-start;
  border: 0;
  background: transparent;
  cursor: pointer;
  &:focus-visible { outline: 3px solid var(--color-text-link); outline-offset: -3px; }
  @media (max-width: 900px) { padding: var(--space-2) var(--space-3); justify-content: center; overflow: hidden; }
`;

const MenuItems = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0 var(--space-2);
  scrollbar-width: thin;
  scrollbar-color: var(--color-sidebar-border) transparent;
`;

const SidebarFooter = styled.div`
  flex-shrink: 0;
  display: flex; align-items: center; flex-wrap: wrap; gap: var(--space-2);
  > button[role="switch"] { flex: 1; width: auto; min-width: 0; }
  > button[aria-controls] { order: 3; flex-basis: 100%; margin: 0; }
  border-top: 1px solid var(--color-sidebar-border);
  margin-top: auto;
  padding: var(--space-2) var(--space-4);

  @media (max-width: 900px) {
    padding: var(--space-1) var(--space-2);
    gap: 0;
    > button[role="switch"], > button:last-child { flex-basis: 100%; width: 100%; }
    .theme-label { display: none; }
  }
`;

const ThemeToggle = styled.button`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
  min-height: var(--touch-target);
  margin-bottom: 0;
  padding: 0;
  border: 0;
  border-radius: var(--radius-md);
  background: transparent;
  color: var(--color-sidebar-text-active);
  font: var(--font-weight-medium) var(--text-body) var(--font-family-base), sans-serif;
  cursor: pointer;
  .theme-label { display: flex; align-items: center; gap: 8px; white-space: nowrap; }
  svg { flex-shrink: 0; }
  &:hover { background: var(--color-sidebar-item-active-bg); }
  &:focus-visible { outline: 3px solid var(--color-text-link); outline-offset: 2px; }
  @media (max-width: 900px) {
    flex-direction: column;
    justify-content: center;
    min-height: var(--touch-target);
    .theme-label span { display: none; }
  }
`;

const ThemeSwitchTrack = styled.span`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  box-sizing: border-box;
  width: 40px;
  height: 24px;
  padding: 3px;
  border: 1px solid var(--color-border-emphasis);
  border-radius: 20px;
  background: var(--color-border-strong);
  &::after { content: ''; width: 16px; height: 16px; border-radius: 50%; background: var(--color-text-on-brand); }
  [aria-checked="true"] & { background: var(--color-brand); border-color: var(--color-text-link); }
  [aria-checked="true"] &::after { transform: translateX(16px); }
`;

const LogoutButton = styled.button`
  display: grid; place-items: center;
  flex-shrink: 0; width: var(--touch-target); min-height: var(--touch-target);
  border: 0; border-radius: var(--radius-md); background: transparent;
  color: var(--color-sidebar-text); cursor: pointer;
  span { position: absolute; width: 1px; height: 1px; padding: 0; overflow: hidden; clip-path: inset(50%); white-space: nowrap; }
  &:hover { background: var(--color-sidebar-item-active-bg); color: var(--color-sidebar-text-active); }
  &:focus-visible { outline: 3px solid var(--color-text-link); outline-offset: 2px; }
`;

const Menu = styled.button.attrs({ type: "button" })`
  width: 100%;
  display: flex;
  align-items: center;
  box-sizing: border-box;
  min-height: var(--touch-target);
  padding: var(--space-2) var(--space-3);
  border-radius: var(--radius-md);
  font-size: var(--text-body);
  line-height: var(--leading-body);
  border: 0;
  background: transparent;
  text-align: left;
  font-family: var(--font-family-base); font-weight: var(--font-weight-semibold);
  white-space: nowrap;
  color: var(--color-sidebar-text);
  cursor: pointer;

  &:focus-visible { outline: 3px solid var(--color-text-link); outline-offset: -3px; }
  &:hover,
  &.this {
    color: var(--color-sidebar-text-active);
    img {
      filter: none;
    }
  }
  img {
    width: var(--space-6);
    height: var(--space-6);
    flex-shrink: 0;
    object-fit: scale-down;
    margin-right: var(--space-2);
    filter: invert(62%) sepia(8%) saturate(0%) hue-rotate(233deg)
      brightness(90%) contrast(84%);
  }
  &.this {
    background: var(--color-sidebar-item-active-bg);
    color: var(--color-sidebar-text-active);
  }

  @media (max-width: 900px) {
    justify-content: center;
    padding: var(--space-2) var(--space-3);
    font-size: 0;
    img { margin-right: 0; }
    span { font-size: var(--text-caption); }
    .menu-label { display: none; }
  }
`;

const ExcessBadge = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--space-5);
  height: var(--space-5);
  margin-left: 0.4rem;
  border-radius: 50%;
  background-color: var(--color-danger-subtle);
  color: var(--color-danger-strong);
  font-size: var(--text-caption);
  font-family: var(--font-family-base); font-weight: var(--font-weight-bold);
  line-height: 1;
`;

const CountBadge = styled(ExcessBadge)`
  width: auto;
  min-width: 18px;
  height: 18px;
  padding: 0 5px;
  border-radius: 9px;
`;

const CoinBalance = styled.div`
  flex-shrink: 0;
  margin: 0 var(--space-4) var(--space-2);
  padding: var(--space-2) var(--space-3);
  border: 1px solid var(--color-sidebar-border);
  border-radius: var(--radius-md);
  background: var(--color-sidebar-item-active-bg);
  color: var(--color-sidebar-text);
  font-size: var(--text-caption);
  line-height: 1.4;

  strong {
    display: block;
    margin-top: 0;
    color: var(--color-sidebar-text-active);
    font-family: var(--font-family-base); font-weight: var(--font-weight-bold);
    font-size: var(--text-body);
    font-variant-numeric: tabular-nums;
  }

  &[role="alert"] strong { color: var(--color-text-danger); font-size: var(--text-caption); }
  @media (max-width: 900px) { display: none; }
`;

interface AdminMenu {
  menuname: string;
  collapsed?: boolean;
  coinRefreshVersion?: number;
  sidebarToggle?: ReactNode;
  hasExcessOrder?: boolean;
}

const Admin_menubar = (props: AdminMenu) => {
  const Navigate = useNavigate();
  const { logout } = useToken();
  const { theme: appearance, toggleTheme: toggleAppearance } = useTheme();
  const menuname = props.menuname;
  const hasExcessOrder = props.hasExcessOrder;
  const [platformAdmin, setPlatformAdmin] = useState(false);
  const [userLevel, setUserLevel] = useState("");
  const [adminPermissions, setAdminPermissions] = useState<string[]>([]);
  const [supportUnread, setSupportUnread] = useState(0);
  const [supportUnreadError, setSupportUnreadError] = useState(false);
  const [coin, setCoin] = useState<number | null>(null);
  const [coinState, setCoinState] = useState<"loading" | "ready" | "error">("loading");
  const supportRequestRef = useRef(0);
  useEffect(() => {
    let active = true;
    api.get(url.Communication_url.user_info).then(response => {
      if (active) {
        setPlatformAdmin(response.data?.user_level === 'linkcam');
        setUserLevel(response.data?.user_level || "");
        setAdminPermissions(response.data?.admin_permissions || []);
      }
    }).catch(() => { if (active) { setPlatformAdmin(false); setUserLevel(""); } });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    api.get(url.Communication_url.stock_coin, { signal: controller.signal }).then(response => {
      const rawBalance = response.data?.coin;
      if (rawBalance == null || rawBalance === "") throw new Error("Missing balance");
      const balance = Number(rawBalance);
      if (!Number.isSafeInteger(balance) || balance < 0) throw new Error("Invalid balance");
      if (active) { setCoin(balance); setCoinState("ready"); }
    }).catch(() => {
      if (active) { setCoin(null); setCoinState("error"); }
    });
    return () => { active = false; controller.abort(); };
  }, [props.coinRefreshVersion]);

  useEffect(() => {
    let active = true;
    const load = () => {
      const request = ++supportRequestRef.current;
      return api.get(url.Communication_url.support_seller_unread_count).then(response => {
        if (active && request === supportRequestRef.current) {
          setSupportUnread(Number(response.data?.unread_count || 0));
          setSupportUnreadError(false);
        }
      }).catch(() => { if (active && request === supportRequestRef.current) setSupportUnreadError(true); });
    };
    void load();
    const timer = window.setInterval(load, 20000);
    const unsubscribe = subscribeSupportActivity(load);
    window.addEventListener("support-unread-refresh", load);
    return () => {
      active = false;
      supportRequestRef.current += 1;
      window.clearInterval(timer);
      unsubscribe();
      window.removeEventListener("support-unread-refresh", load);
    };
  }, []);

  const go_dashboard = () => Navigate("/admin/dashboard");
  const go_livelist = () => Navigate("/admin/livelist");
  const go_orderlist = () => Navigate("/admin/order-list");
  const go_livesales = () => Navigate("/admin/live-sales");
  const go_userlist = () => Navigate("/admin/userlist/user");
  const go_coinmanage = () => Navigate("/admin/coinmanage");
  const go_customerboard = () => Navigate("/admin/customer-board");
  const go_staff = () => Navigate("/admin/staff");
  const go_setting = () => Navigate("/admin/setting");
  const isMaster = userLevel === "master";
  const can = (permission: SellerAdminPermissionKey) => isMaster || adminPermissions.includes(permission);

  return (
    <MenuBox data-collapsed={props.collapsed || undefined}>
      <LogoButton data-sidebar-logo type="button" onClick={go_dashboard} aria-label="대시보드로 이동">
        <picture>
          <source media="(max-width: 900px)" srcSet={require("../../Image/Logo.png")} />
          <Icon style={{ height: 44 }} src={props.collapsed ? require("../../Image/Logo.png") : require("../../Image/Logo_Text.png")} alt="FanBird" />
        </picture>
      </LogoButton>

      {isMaster && !props.collapsed && <CoinBalance data-sidebar-coin
        aria-busy={coinState === "loading"}
        aria-live="polite"
        role={coinState === "error" ? "alert" : "status"}
      >
        보유 코인
        <strong>{coinState === "loading" ? "조회 중…" : coinState === "error" ? "조회 실패" : `${coin?.toLocaleString()} C`}</strong>
      </CoinBalance>}

      <MenuItems aria-label="관리자 메뉴">
      {platformAdmin && <Menu title="결제 취소" aria-label="결제 취소" className={menuname === '결제 환불' ? 'this' : ''} aria-current={menuname === '결제 환불' ? 'page' : undefined} onClick={() => Navigate('/admin/payment-cancel')}>
        <Icon src={require('../../Image/Admin/ic_menu_shopping.png')} />
        <span className="menu-label">결제 취소</span>
      </Menu>}

      {isMaster && <Menu title="대시보드" aria-label="대시보드"
        className={menuname === "대시보드" ? "this" : ""}

        aria-current={menuname === "대시보드" ? "page" : undefined}
        onClick={go_dashboard}
      >
        <Icon src={require("../../Image/Admin/ic_menu_board.png")} />
        <span className="menu-label">대시보드</span>
      </Menu>}
      {can("menu_live") && <Menu title="라이브 목록" aria-label="라이브 목록"
        className={menuname === "라이브 목록" ? "this" : ""}

        aria-current={menuname === "라이브 목록" ? "page" : undefined}
        onClick={go_livelist}
      >
        <Icon src={require("../../Image/Admin/ic_menu_live.png")} />
        <span className="menu-label">라이브 목록</span>
      </Menu>}
      {isMaster && <Menu title="상품 창고" aria-label="상품 창고"
        className={menuname === "상품 창고" ? "this" : ""}
        aria-current={menuname === "상품 창고" ? "page" : undefined}
        onClick={() => Navigate('/admin/products')}>
        <svg className="warehouse-menu-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="m3 7 9-4 9 4v10l-9 4-9-4V7Zm0 0 9 4 9-4M12 11v10M7.5 5 17 9" /></svg>
        <span className="menu-label">상품 창고</span>
      </Menu>}
      {can("menu_orders") && <Menu title="주문내역" aria-label="주문내역"
        className={menuname === "주문내역" ? "this" : ""}

        aria-current={menuname === "주문내역" ? "page" : undefined}
        onClick={go_orderlist}
      >
        <Icon src={require("../../Image/Admin/ic_menu_shopping.png")} />
        <span className="menu-label">주문내역</span>
        {hasExcessOrder && <ExcessBadge>!</ExcessBadge>}
      </Menu>}
      {can("menu_analytics") && <Menu title="데이터 분석" aria-label="데이터 분석" className={menuname === "데이터 분석" ? "this" : ""} aria-current={menuname === "데이터 분석" ? "page" : undefined} onClick={() => Navigate("/admin/analytics")}><svg className="warehouse-menu-icon" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M4 3v17h17M8 15V9m5 6V5m5 10v-4" /></svg><span className="menu-label">데이터 분석</span></Menu>}
      {isMaster && <Menu title="라이브별 매출" aria-label="라이브별 매출"
        className={menuname === "라이브별 매출" ? "this" : ""}
        aria-current={menuname === "라이브별 매출" ? "page" : undefined}
        onClick={go_livesales}
      >
        <Icon src={require("../../Image/Admin/ic_menu_shopping.png")} />
        <span className="menu-label">라이브별 매출</span>
      </Menu>}
      {can("menu_members") && <Menu title="회원목록" aria-label="회원목록"
        className={menuname === "회원목록" ? "this" : ""}

        aria-current={menuname === "회원목록" ? "page" : undefined}
        onClick={go_userlist}
      >
        <Icon src={require("../../Image/Admin/ic_menu_people.png")} />
        <span className="menu-label">회원목록</span>
      </Menu>}
      {can("menu_coin") && <Menu title="코인 관리" aria-label="코인 관리"
        className={menuname === "코인 관리" ? "this" : ""}

        aria-current={menuname === "코인 관리" ? "page" : undefined}
        onClick={go_coinmanage}
      >
        <Icon src={require("../../Image/Admin/ic_menu_coin.png")} />
        <span className="menu-label">코인 관리</span>
      </Menu>}
      {can("menu_inquiry") && <Menu title="1:1문의" aria-label="1:1문의"
        className={menuname === "1:1문의" ? "this" : ""}
        aria-current={menuname === "1:1문의" ? "page" : undefined}
        onClick={go_customerboard}
      >
        <Icon src={require("../../Image/Admin/ic_menu_customer_board.png")} />
        <span className="menu-label">1:1문의</span>
        {supportUnread > 0 ? <CountBadge role="status" aria-live="polite" aria-atomic="true" aria-label={`읽지 않은 1:1문의 ${supportUnread}개`}>{supportUnread > 99 ? "99+" : supportUnread}</CountBadge>
          : supportUnreadError && <CountBadge role="alert" title="읽지 않은 1:1문의 수를 불러오지 못했습니다" aria-label="읽지 않은 1:1문의 수를 불러오지 못했습니다">!</CountBadge>}
      </Menu>}
      {(can("account_manage") || can("account_permission")) && <Menu title="계정 관리" aria-label="계정 관리" className={menuname === "계정 관리" ? "this" : ""} aria-current={menuname === "계정 관리" ? "page" : undefined} onClick={go_staff}>
        <Icon src={require("../../Image/Admin/ic_menu_people.png")} />
        <span className="menu-label">계정 관리</span>
      </Menu>}

      {can("menu_settings") && <Menu title="설정" aria-label="설정" className={menuname === "설정" ? "this" : ""} aria-current={menuname === "설정" ? "page" : undefined} onClick={go_setting}>
        <Icon src={require("../../Image/Admin/ic_menu_setting.png")} />
        <span className="menu-label">설정</span>
      </Menu>}
      </MenuItems>
      <SidebarFooter data-sidebar-footer>
        <ThemeToggle type="button" role="switch" aria-label="다크 모드" aria-checked={appearance === "dark"} title="다크 모드" onClick={toggleAppearance}>
          <span className="theme-label">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M20 15.3A8.5 8.5 0 0 1 8.7 4a8.5 8.5 0 1 0 11.3 11.3Z" stroke="currentColor" strokeWidth="1.7" strokeLinejoin="round" /></svg>
            <span>다크 모드</span>
          </span>
          <ThemeSwitchTrack aria-hidden="true" />
        </ThemeToggle>
        {props.sidebarToggle}
        <LogoutButton aria-label="로그아웃" title="로그아웃" type="button" onClick={() => logout("ok")}>
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.7" aria-hidden="true"><path d="M10 4H5v16h5M14 8l4 4-4 4M8 12h10" strokeLinecap="round" strokeLinejoin="round" /></svg>
          <span>로그아웃</span></LogoutButton>
      </SidebarFooter>

    </MenuBox>
  );
};

export default Admin_menubar;
